import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Pill, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Compact section shown inside PatientProfileModal listing this patient's
 * pharmacy sales (inbound from Eden Pharmacy). Default view: latest 25.
 * Click to expand for items, payments and reward outcome.
 */
const PatientPharmacySalesSection = ({ patientId }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const res = await api.get('/pharmacy/sales', {
        params: { patient_id: patientId, limit: 25 },
      });
      setRows(res.data?.rows || []);
      setForbidden(false);
    } catch (e) {
      if (e.response?.status === 403) {
        // Non-admin — hide entire section silently.
        setForbidden(true);
        setRows([]);
      } else {
        toast.error('Failed to load pharmacy sales');
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  if (!patientId) return null;
  // Only hide for non-admins (API returned 403). Admins see the section
  // even when the patient has no sales, matching the Packages section UX.
  if (forbidden) return null;

  const totalSpend = rows.reduce((s, r) => s + Number(r.totals?.paid || 0), 0);
  const totalPoints = rows.reduce((s, r) => {
    if (r.reward_last_action === 'awarded') return s + Number(r.reward_points_earned_last || 0);
    if (r.reward_last_action === 'reversed') return s + Number(r.reward_points_earned_last || 0);
    return s;
  }, 0);

  return (
    <div data-testid="patient-pharmacy-sales-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Pill className="w-5 h-5 text-teal-600" />
          Pharmacy Sales
          {rows.length > 0 && (
            <Badge className="bg-teal-100 text-teal-700 text-xs">{rows.length}</Badge>
          )}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="h-7"
          data-testid="patient-pharm-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Lifetime summary chips */}
      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 text-xs">
            <span className="font-medium text-emerald-700">Total paid: </span>
            <span className="font-bold text-emerald-800">₹{totalSpend.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-full px-3 py-1 text-xs">
            <span className="font-medium text-fuchsia-700">Reward points: </span>
            <span className={`font-bold ${totalPoints >= 0 ? 'text-fuchsia-800' : 'text-rose-700'}`}>
              {totalPoints > 0 ? '+' : ''}{totalPoints}
            </span>
          </div>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-center py-4 text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl" data-testid="patient-pharm-empty">
          No pharmacy sales for this patient yet.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => {
          const isOpen = expandedId === r.sale_id;
          const paid = Number(r.totals?.paid || 0);
          const total = Number(r.totals?.total || 0);
          const ps = String(r.totals?.payment_status || '').toLowerCase();
          const isCancelled = r.status === 'cancelled';
          const isFullyPaid = ps === 'paid' || (total > 0 && paid >= total);
          const statusCls = isCancelled
            ? 'bg-rose-100 text-rose-700'
            : (isFullyPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700');
          const statusLabel = isCancelled ? 'Cancelled' : (isFullyPaid ? 'Paid' : 'Partial Paid');

          return (
            <div key={r.sale_id} className="bg-white border border-teal-100 rounded-xl overflow-hidden" data-testid={`patient-pharm-${r.sale_id}`}>
              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : r.sale_id)}
                className="w-full p-3 hover:bg-teal-50/40 flex items-center gap-3 text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-teal-700 bg-teal-50 px-2 py-0.5 rounded">{r.receipt_no || r.sale_id}</span>
                    <Badge className={`${statusCls} text-[10px]`}>{statusLabel}</Badge>
                    <span className="text-[11px] text-gray-500">
                      {String(r.created_at || r.received_at || '').slice(0, 10)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 mt-0.5">
                    <span>Paid <span className="font-semibold text-emerald-700">₹{Number(r.totals?.paid || 0).toLocaleString()}</span></span>
                    {' / '}
                    <span>Total ₹{Number(r.totals?.total || 0).toLocaleString()}</span>
                    {r.reward_last_action === 'awarded' && (
                      <span className="ml-2 text-fuchsia-700 font-semibold">+{r.reward_points_earned_last} pts</span>
                    )}
                    {r.reward_last_action === 'reversed' && (
                      <span className="ml-2 text-rose-700 font-semibold">{r.reward_points_earned_last} pts</span>
                    )}
                  </p>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>

              {isOpen && (
                <div className="px-3 pb-3 space-y-2 border-t border-teal-50 pt-2">
                  {/* Items */}
                  {(r.items || []).length > 0 && (
                    <div className="space-y-0.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Items</div>
                      {(r.items || []).map((it, i) => (
                        <div key={i} className="flex justify-between text-xs text-gray-700">
                          <span className="truncate pr-1">
                            {it.medicine || it.sku}
                            {it.batch_display && <span className="text-[10px] text-gray-500 ml-1">· {it.batch_display}</span>}
                          </span>
                          <span className="font-mono text-gray-500 whitespace-nowrap">
                            {it.qty} × ₹{Number(it.unit_price || 0).toLocaleString()} = ₹{Number(it.total || 0).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Totals mini-row */}
                  <div className="grid grid-cols-3 gap-2 text-[11px] pt-1">
                    <div className="text-gray-500">Subtotal <span className="font-semibold text-gray-800 block">₹{Number(r.totals?.subtotal || 0).toLocaleString()}</span></div>
                    <div className="text-gray-500">Discount <span className="font-semibold text-orange-700 block">₹{Number(r.totals?.discount || 0).toLocaleString()}</span></div>
                    <div className="text-gray-500">Balance <span className={`font-semibold block ${Number(r.totals?.balance || 0) > 0 ? 'text-orange-700' : 'text-emerald-700'}`}>₹{Number(r.totals?.balance || 0).toLocaleString()}</span></div>
                  </div>

                  {/* Payments */}
                  {(r.payments || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(r.payments || []).map((p, i) => (
                        <span key={i} className="bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5 text-[10px]">
                          <span className="font-semibold text-emerald-800">{p.method_code || p.method_name || '—'}</span>
                          <span className="ml-1 text-emerald-700">₹{Number(p.amount || 0).toLocaleString()}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Cashier + timestamps */}
                  <div className="text-[10px] text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5 pt-1">
                    {r.actor?.user_name && <span>Cashier: {r.actor.user_name}</span>}
                    <span className="font-mono">sale_id: {r.sale_id}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PatientPharmacySalesSection;
